<script>
	import Form from './components/Form.svelte';

	const phylogenies = [
		'Archaeal',
		'Bacterial',
	];

	const taxonomicRanks = [
		'species',
		'genus',
		'family',
	];


	const phylogenyReleaseVersions = [
		'207',
		'202',
	];

	const apiUrl = API_URL ?? '/api';

	// Form fields
	let fields = [
		{
			component: 'Html',
			value: '<h5>Main settings (required)</h5>',
		},
		{
			component: 'Select',
			defaultOption: 'Select a phylogeny type...',
			id: 'phylogenyType',
			label: 'Phylogeny type<strong>†</strong>',
			name: 'phylogenyType',
			options: phylogenies,
			required: true,
			value: '',
		},
		{
			component: 'Select',
			defaultOption: 'Select a taxonomic rank...',
			id: 'taxonomicRank',
			label: 'Taxonomic rank to map your input',
			name: 'taxonomicRank',
			options: taxonomicRanks,
			required: true,
			value: '',
		},
		{
			component: 'TextInput',
			id: 'emailAddress',
			label: 'Your email address<strong>††</strong>',
			name: 'emailAddress',
			placeholder: 'your@email.com',
			required: true,
			type: 'email',
			value: '',
		},
		{
			component: 'FileInput',
			id: 'classificationFile',
			files: null,
			label: 'Input taxID file',
			name: 'classificationFile',
			required: true,
		},
		{
			component: 'Html',
			value:'(click <a download href="/files/sample_taxIDs.txt">here</a> to download example input for a list of bacterial taxonomy IDs.)',
		},
		{
			component: 'Html',
			value: '<hr style="margin-bottom: calc(2 * var(--spacing));">',
		},
		{
			component: 'Html',
			value: '<h5>Advanced settings††† (optional)</h5>',
		},
	];

	let advancedFields = [
		{
			component: 'Select',
			id: 'phylogenyReleaseVersion',
			label: 'Phylogeny Release Version',
			name: 'phylogenyReleaseVersion',
			options: phylogenyReleaseVersions,
			required: true,
			value: '207', // default value
		},
		{
			component: 'TextInput',
			defaultValue: 1,
			id: `threshold1`,
			label: `Threshold 1 (Species-level reference group)`,
			name: `threshold1`,
			placeholder: '1 (Default)',
			type: 'text',
			value: '',
		},
		{
			component: 'TextInput',
			defaultValue: 2,
			id: `threshold2`,
			label: `Threshold 2 (Species group-level reference group)`,
			name: `threshold2`,
			placeholder: '2 (Default)',
			type: 'text',
			value: '',
		},
		{
			component: 'TextInput',
			defaultValue: 2,
			id: `threshold3`,
			label: `Threshold 3 (Genus and above-level reference group)`,
			name: `threshold3`,
			placeholder: '2 (Default)',
			type: 'text',
			value: '',
		},
		{
			component: 'TextInput',
			defaultValue: 0.75,
			id: `threshold4`,
			label: `Threshold 4 (Reference group with only two tips)`,
			name: `threshold4`,
			placeholder: '0.75 (Default)',
			type: 'text',
			value: '',
		},
	];
	let promise;

	// Send form data to API and get response
	let getResults = async (payload) => {
		const response = await fetch(`${apiUrl}/file`, payload);
		const results = await response;

		if (response.ok) {
			// the automated file download present in previous versions has been removed. The results will be sent as a download link from the backend.
			alert("Thank you for submitting your request to PhyloPlus. The results should be available within a few minutes to an hour and will be sent to the email address you provided from jifsan-services@umd.edu. If an error occurs in processing your request, the application will attempt to notify you by email.");
		} else {
			const errorMessage = await response.text();
			alert('Error: ' + errorMessage);
			throw new Error(response.text());
		}
	};

	// Submission behavior for the form
	let handleSubmit = (formData) => {
		let payload = {
			method: 'POST',
			body: formData,
		};

		promise = getResults(payload);
	}
</script>

<svelte:head>
	<link rel="stylesheet" href="pico.min.css">
</svelte:head>

<header style="align-items: center; display: flex; justify-content: center; padding: calc(3.5 * var(--block-spacing-vertical)); position: relative;">
	<img alt="Banner" src="/img/web_phylo.webp" style="display: block; height: 100%; margin-left: auto; margin-right: auto; padding: 0; object-fit: contain; position: absolute; top: 0; width: 100%;">
	<nav>
		<span></span>
		<span></span>
		<span></span>
	</nav>
</header>

<hr>

<main class="container" style="padding-top: calc(var(--block-spacing-vertical) - 0.5em);">
	<article style="margin-top: 0;">
		The manuscript is published online in <a href="https://journals.asm.org/doi/10.1128/mbio.03455-22"><em>mBio</em></a>. If you find this web tool useful, please kindly cite the original manuscript: <br><br>
		Huang, X., Erickson, D. L., & Meng, J. (2023). PhyloPlus: a Universal Tool for Phylogenetic Interrogation of Metagenomic Communities. <i>mBio</i>, 14(1), e0345522. <a href="https://doi.org/10.1128/mbio.03455-22">https://doi.org/10.1128/mbio.03455-22</a>		
		<br><br><br>
		<font size="-0.5">We suggest <a href="/files/thresholds.pdf">setting a threshold</a> for metagenomic classifiers before uploading your input to generate the phylogeny. This helps to exclude most false positive identifications of rare taxa and prevents potential formation of polytomies for these taxa.
		</font>

	</article>
	
	
	<article>
		<h2>Updates</h2>
		<p>
			<font>
				<b>April 25, 2023:</b><br />
				<ul>
					<li style="list-style-type: circle;">
				K-means clustering is applied to select a subcluster to infer the locations of taxa mapped to diverse groups (e.g., <i>Pseudomonas</i>) in order to avoid biases towards long branch lengths due to 
				formation of polytomies in calculation of terminal phylogeny metrics. 
					</li>
				</ul>
			</font>
		</p>
			<p>
				<font size="-1">
					<b>February 3, 2023:</b><br>
					<ul>
					<li style="list-style-type: circle;">
					To reduce running time, the current version no longer automatically maps species names identified in the original reference phylogeny to the final output. Users may provide a list containing taxa in the background microbiome
					to generate a comprehensive phylogeny for uses like null model analysis.
					</li>
					<li style="list-style-type: circle;">
					It's now possible for the user to choose the taxonomic rank they want to map in the final phylogenies. Lower-level taxonomy IDs will be automatically converted and processed. For example, including taxonomy ID 562 (<i>Escherichia coli</i>) in 
					the input and selecting genus as the taxonomic rank will automatically convert the species-level ID into its genus-level ID (561), but not vice versa.
					</li>
					</ul>
				</font>
			</p>
		
	</article>
	<article>
		<h1 id="intro">Introduction</h1>
		<p>
			This web application will create a phylogeny where user specified taxa will be added to a publicly available, peer reviewed molecular bacterial or archaeal phylogeny.
			This approach takes advantage of the comprehensive bacterial/archaeal molecular phylogenetic trees generated by
			<a href="https://doi.org/10.1038/nbt.4229">Genome Taxonomy Database (GTDB)</a>, and allows users to submit their own customized set of bacterial or archaeal taxa that will be added to either of these phylogenies.
			The set of taxa to add is specified in the form of a columnized plain text file with each line containing the NCBI taxonomy ID for a single query taxon.
		</p>

		<p>
			The addition of taxa to the original phylogeny is based on complete taxonomic lineage information that is appended to all tips present in the tree, as well as
			lineage information retrieved for query taxa. Please note that this web tool uses separate reference phylogenies for bacteria and archaea, please select the reference
			phylogeny accordingly.
		</p>


		The output generated by this web tool is a file folder containing: 
		<ol type="a" style="padding-left: calc(2 * var(--spacing))">
			<li>
				2 Newick tree files including:
				<ul>
					<li>
						one phylogeny from user input with scientific names as tip labels (scientific_names.tree);
					</li>
					<li>
						another phylogeny from user input with taxonomy IDs as tip labels (taxIDs.tree).
					</li>
				</ul>
			</li>
			<li>
				1 summary file for the insertion of taxa provided in the user input, detailing the taxonomic rank with which a taxon can be mapped to the reference phylogeny, its assigned branch length, and the number of reference tips used for the inference (insertion_summary.csv);
			</li>
			<li>
				1 text file recording any potential changes made to the user input taxonomy IDs, e.g., outdated or merged taxIDs (note.txt).
			</li>
		</ol>
		<p>
			For a more detailed description of this process, please see <a href="https://journals.asm.org/doi/suppl/10.1128/mbio.03455-22/suppl_file/mbio.03455-22-s0001.pdf">the supplementary information</a>. The python and R code
			realization of this process can be found at <a href="https://github.com/Dennis-xyHuang/PhyloPlus">https://github.com/Dennis-xyHuang/PhyloPlus</a>, in case the most
			up-to-date taxonomic and lineage information is needed.
		</p>
	</article>
	<article>
		<header>
			To generate your custom phylogeny, please complete the following information:
		</header>
		<Form bind:fields bind:advancedFields enctype="multipart/form-data" onSubmit={handleSubmit} submissionPromise={promise} />
		<footer>
			<p>
				<small>
					† Lineage information was last updated on July 28, 2023.
				</small>
			</p>
			<p>
				<small>
					†† Email address is kept confidential and only used per <a href="https://www.ncbi.nlm.nih.gov/books/NBK25497/#chapter2.Usage_Guidelines_and_Requirement">NCBI Entrez requirements</a> for batch data retrieval,
					for sending final output, and for error reporting.
				</small>
			</p>
			<p>
				<small>
					††† These optional parameters affect detection and removal of outlier tips. For more information, please refer to the links in the <a href="#intro">introductory section</a>.
				</small>
			</p>
		</footer>
	</article>
</main>

<footer style="background-color: var(--primary); color: var(--primary-inverse);">
	<nav>
		<span></span>
		<span>© 2023 University of Maryland and Xinyang Huang</span>
		<span></span>
	</nav>
</footer>
